import { useCallback, useState, useEffect } from "react";

import { PageModule, SurveyQuestion, SurveyQuestionOption } from "../../../../../types/working-model";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import classNames from 'classnames';
import SurveyOption from "./survey-option";
import { getStylingOverrides } from "../../../utils";
import { ParamsProps } from "../../../live-event";
import { useParams } from "react-router";
import { TemplateClassNames } from "../../../../../types/template-layouts";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { CreateSurveyAnswer } from "../../../../../connection/save-survey-answer";
import { GetQuestionStats } from "../../../../../connection/surveys";
import { useDispatch } from "react-redux";
import { setCompletedMultiQuestion } from "../../../../../store/actions/event/event-actions";
import { getStorageItem } from "../../../../../utils/local-storage";
import { useTranslation } from "react-i18next";

interface ICheckboxSurveyQuestionProps {
	question: SurveyQuestion;
	module: PageModule;
	template: TemplateClassNames;
	showResults: boolean;
	sessionUuid: string;
}

const CheckboxSurveyQuestion: React.FC<ICheckboxSurveyQuestionProps> = ({ question, module, template, showResults, sessionUuid }) => {

	const [selected, setSelected] = useState<{ [key: string]: number }>({});
	const params: ParamsProps = useParams();
	const language = params?.language || 'en';
	const { content, styling_overrides } = module;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		primaryButtonOverrides,
	} = stylingOverrides;

	const { event, sessions, registrationId, userSession, registration_on, multiQuestionsCompleted, workingSession, previewMode } = useTypedSelector(state => ({
		event: state.LiveEventReducer.eventBundle?.event,
		sessions: state.LiveEventReducer.eventBundle?.sessions,
		registrationId: state.LiveEventReducer.registrationId,
		userSession: state.LiveEventReducer.userSession,
		registration_on: state.LiveEventReducer.eventBundle?.registration_on,
		multiQuestionsCompleted: state.LiveEventReducer.multiQuestionsCompleted,
		workingSession: state.CreateEventReducer.workingSession,
		previewMode: state.CreateEventReducer.previewMode
	}));

	const dispatch = useDispatch();

	const [percentages, setPercentages] = useState<{ [key: number]: number }>({});
	const [totalAnswers, setTotalAnswers] = useState(0);
	const [submitDisabled, setSubmitDisabled] = useState(false);
	const [showPercentages, setShowPercentages] = useState(false);
	const [percentagesIncremented, setPercentagesIncremented] = useState(false);

	const { t } = useTranslation("session");

	const getSession = useCallback(() => {
		return sessions?.find(session => session.uuid === sessionUuid);
	}, [sessions, sessionUuid]);

	const session = previewMode === 'session' ? workingSession : getSession();

	const token = getStorageItem('Bl-Profile-Token');

	useEffect(() => {
		// if there are answers in the store, updated selected options with those answers
		if (multiQuestionsCompleted.length) {
			for (let i = 0; i < multiQuestionsCompleted.length; i++) {
				if (multiQuestionsCompleted[i].question === question.survey_question) {
					setSelected(multiQuestionsCompleted[i].options);

					// If there are options selected in the store, disable the submit button
					// and options responsivness to avoid double submissions
					if (!percentagesIncremented && percentages) {
						incrementPercentages(Object.keys(multiQuestionsCompleted[i].options));
						setPercentagesIncremented(true);
					}
					setSubmitDisabled(true);
					if (showResults) {
						setShowPercentages(true);
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [multiQuestionsCompleted, question.survey_question, showResults]);

	useEffect(() => {
		// This is the initial call to grab all of the submission totals for each option and set the state
		if (session) {
			const getPercentages = async () => {
				const result = await GetQuestionStats(session?.session, question.survey_question, question.question_type);
				const array: { [key: string]: number } = {};
				for (const [key, value] of Object.entries(result.counts)) {
					array[key] = value;
				}
				setPercentages(array);
				setTotalAnswers(result.total);
			};

			getPercentages();
		}
	}, [session, question]);

	const handleChange = (val: string | number) => {
		// handles selecting an option
		const value = val as number;
		setSelected(prev => {
			const updated = { ...prev };
			if (!prev[value]) {
				updated[value] = value;
			} else {
				delete updated[value];
			}
			return updated;
		});
	};

	const percentage = (option: SurveyQuestionOption) => {
		// this function returns a formatted value to reprsent the percentage
		if (totalAnswers && percentages[option.survey_question_option]) {
			return Math.floor(percentages[option.survey_question_option] / totalAnswers * 100);
		}
		else
			return 0;
	};

	const renderOptions = () => {
		return question.options.map(option => (
			<div className="survey-option-wrapper" key={option.survey_question_option}>
				<SurveyOption
					value={option.survey_question_option}
					title={option.label?.[language] as string ?? option.label.base}
					selected={Object.values(selected).includes(option.survey_question_option)}
					onClick={handleChange}
					percentage={showPercentages ? percentage(option) : null}
					disabled={submitDisabled}
				/>
			</div>
		));
	};

	function incrementPercentages(options: string[]) {
		// this function increments the local state to reflect the options selected being
		// added to the values pulled from the api, without having to call the api again
		const newPercentages = percentages;
		for (let i = 0; i < options.length; i++) {
			newPercentages[Number(options[i])] = (1 + newPercentages[Number(options[i])]) || 1;
			setPercentages(newPercentages);
		}
		setTotalAnswers(totalAnswers + 1);
	}

	const handleSubmit = () => {
		// this function handles the submission of options selected
		if (question.required && !Object.entries(selected).length) return;
		let body = {};

		if (registration_on) {
			// if registration for the event is turned on, submit the
			// userSession and registrationId as well
			body = {
				session: session?.session,
				survey_question: question.survey_question,
				module: module.id,
				event: event,
				text_answer: null,
				radio_answer: null,
				checkbox_answer: Object.keys(selected),
				session_uuid: session?.uuid,
				language: language,
				userSession: userSession,
				registration_id: registrationId
			};
		} else {
			body = {
				session: session?.session,
				survey_question: question.survey_question,
				module: module.id,
				event: event,
				text_answer: null,
				radio_answer: null,
				checkbox_answer: Object.keys(selected),
				session_uuid: session?.uuid,
				language: language
			};
		}
		// Send the message to microservices
		CreateSurveyAnswer(body, token);
		incrementPercentages(Object.keys(selected));
		setPercentagesIncremented(true);

		// update the store with the selected options
		dispatch(setCompletedMultiQuestion(question.survey_question, selected));
		setSubmitDisabled(true);
		if (showResults) {
			setShowPercentages(true);
		}
	};

	if (!session && (previewMode !== 'session')) return null;

	return (
		<div className={classNames('question-container')}>
			<TypographyItem className={classNames('question-title evt-heading-4', template)} tagName="p">
				{question?.title?.[language] ?? question?.title?.base}
			</TypographyItem>
			<div>{renderOptions()}</div>
			<div className={classNames('submit-action')}><button className={classNames('primary next', primaryButtonOverrides)} type="submit" onClick={handleSubmit} disabled={submitDisabled || !Object.keys(selected).length || (previewMode === 'session')}>{content?.callToAction?.button_name?.[language] || t("Submit")}</button></div>
		</div>
	);
};

export default CheckboxSurveyQuestion;
